import React, { FC, useEffect, useRef, useState } from "react";
import {
  Alert,
  Typography,
  Box,
  ThemeProvider,
  Link,
  Checkbox,
  FormGroup,
  FormControlLabel,
  FormLabel,
  Divider,
  Button,
  Stack,
  Input,
  Grid,
  ButtonProps,
  Radio,
  RadioGroup,
} from "@mui/material";
import { theme, TextInput } from "../habitat";
import { styled } from "@mui/material/styles";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload } from "@fortawesome/pro-regular-svg-icons";
import {
  faCircleExclamation,
  faXmark,
  faCheck,
} from "@fortawesome/pro-solid-svg-icons";
import { faCircleExclamation as faCircleExclamationDuo } from "@fortawesome/pro-duotone-svg-icons";
import { SsoButton, SsoButtonText } from "./Styled";
import FloModal from "../Components/FloModal";
import { saveCompanySsoSettings } from "./SsoApi";
import { getDataUrl } from "./getDataUrl";

const ellipseGray = require("assets/images/sso/ellipse_gray.svg");

// FLO-15512: the Custom Company SSO protocol choice. Mirrors the backend's
// `models.sso.SsoProtocol` enum (see `CompanySsoSettingsRequest#getProtocol`).
type SsoProtocol = "OIDC" | "SAML";

// FLO-15509 follow-up: which login-initiation direction(s) a SAML connection accepts. Mirrors
// the backend `com.floify.app.features.saml.domain.FlowMode` enum. SP_ONLY is the safe default
// (today's behavior); IDP_ONLY is for IdPs that won't accept a Floify-issued AuthnRequest (e.g.
// Alkami), and also hides the "Continue with X" borrower login button since it would be a
// dead-end; BOTH accepts either.
type SamlFlowMode = "SP_ONLY" | "IDP_ONLY" | "BOTH";

interface SsoSettings {
  ssoEnabled: boolean;
  facebookEnabled: boolean;
  googleEnabled: boolean;
  appleEnabled: boolean;
  customEnabled: boolean;
  protocol: SsoProtocol;
  pkceEnabled?: boolean;
  wellKnownEndpoint?: string;
  clientId?: string;
  clientSecret?: string;
  apiKey?: string;
  companyName?: string;
  logoUrl?: string;
  // SAML-specific fields (FLO-15512), only relevant when protocol === "SAML".
  idpEntityId?: string;
  idpSsoUrl?: string;
  idpSigningCertificate?: string;
  spEntityId?: string;
  acsUrl?: string;
  // A signed SAML Response pasted in by the admin to verify the IdP connection before saving
  // (FR-1's "Test Company SSO" verification, wired to CompanySsoSettingsSaveService). Never
  // pre-populated from initialSettings -- the backend never persists it.
  samlVerificationResponse?: string;
  flowMode?: SamlFlowMode;
}

interface OidcProvider {
  clientId: string;
  clientPassword: string;
  serverUrl: string;
  key: string;
  usePkce: boolean;
  providerName: string;
  providerIcon: string;
}

// Mirrors `models.sso.SamlProviderModel`'s transport shape.
interface SamlProvider {
  idpEntityId: string;
  idpSsoUrl: string;
  idpSigningCertificate: string;
  spEntityId: string;
  acsUrl: string;
  providerName?: string;
  providerIcon?: string;
  // Display-only metadata about the certificate already on file (ISO-8601 date string, and
  // `CertificateStatus` name e.g. "ACTIVE"/"RETIRING") -- never sent back on save.
  certificateNotAfter?: string;
  certificateStatus?: string;
  flowMode?: SamlFlowMode;
}

interface SsoSettingsRequest {
  appleAvailable: boolean;
  ssoEnabled: boolean;
  appleEnabled: boolean;
  facebookEnabled: boolean;
  googleEnabled: boolean;
  customEnabled: boolean;
  customProvider: OidcProvider;
  protocol?: SsoProtocol;
  samlProvider?: SamlProvider;
}

interface SsoSettingsParameters {
  initialSettings: SsoSettingsRequest;
  // Whether the SAML rollout flag (Feature.CUSTOM_COMPANY_SSO_SAML, t2/FLO-15511) is enabled for
  // this company. The protocol selector is only offered when this is true (FR-1) -- while false,
  // this page behaves exactly as it did before FLO-15512 (OIDC only, no selector).
  samlAvailable?: boolean;
}

const LogoInput = styled(Input)`
  display: none;
`;

const LogoImage = styled("img")`
  position: absolute;
  top: -100000px;
  left: -100000px;
`;

const isNotBlank = (value: string | undefined): boolean =>
  !!value && value.trim() != "";

const fromSettingsRequest = (request: SsoSettingsRequest): SsoSettings => {
  return {
    ssoEnabled: request.ssoEnabled,
    appleEnabled: request.appleEnabled,
    facebookEnabled: request.facebookEnabled,
    googleEnabled: request.googleEnabled,
    customEnabled: request.customEnabled,
    protocol: request.protocol === "SAML" ? "SAML" : "OIDC",
    clientId: request.customProvider?.clientId,
    clientSecret: request.customProvider?.clientPassword,
    wellKnownEndpoint: request.customProvider?.serverUrl,
    apiKey: request.customProvider?.key,
    pkceEnabled: request.customProvider?.usePkce,
    companyName: request.samlProvider?.providerName ?? request.customProvider?.providerName,
    logoUrl:
      request.samlProvider?.providerIcon ||
      request.customProvider?.providerIcon ||
      ellipseGray,
    idpEntityId: request.samlProvider?.idpEntityId,
    idpSsoUrl: request.samlProvider?.idpSsoUrl,
    idpSigningCertificate: request.samlProvider?.idpSigningCertificate,
    spEntityId: request.samlProvider?.spEntityId,
    acsUrl: request.samlProvider?.acsUrl,
    flowMode: request.samlProvider?.flowMode ?? "SP_ONLY",
  };
};

const toSettingsRequest = (state: SsoSettings, samlAvailable: boolean) => {
  // Mirrors evaluateFormValidity's notion of the *effective* protocol: if the SAML rollout flag
  // is off, the SAML fields aren't shown or editable, so a stale state.protocol === "SAML" (e.g.
  // left over from before the flag was disabled) must not cause stale/never-shown SAML data to
  // be submitted alongside freshly-entered OIDC fields.
  const effectiveProtocol: SsoProtocol =
    samlAvailable && state.protocol === "SAML" ? "SAML" : "OIDC";

  return {
    ssoEnabled: state.ssoEnabled,
    appleEnabled: state.appleEnabled,
    facebookEnabled: state.facebookEnabled,
    googleEnabled: state.googleEnabled,
    customEnabled: state.customEnabled,
    protocol: effectiveProtocol,
    customProvider: {
      clientId: state.clientId,
      clientPassword: state.clientSecret,
      serverUrl: state.wellKnownEndpoint,
      key: state.apiKey,
      usePkce: state.pkceEnabled,
      providerName: state.companyName,
      providerIcon: state.logoUrl,
    },
    // Only sent for the SAML protocol -- CompanySsoSettingsSaveService requires both when
    // protocol === SAML, and ignores them otherwise.
    ...(effectiveProtocol === "SAML"
      ? {
          samlProvider: {
            idpEntityId: state.idpEntityId,
            idpSsoUrl: state.idpSsoUrl,
            idpSigningCertificate: state.idpSigningCertificate,
            spEntityId: state.spEntityId,
            acsUrl: state.acsUrl,
            providerName: state.companyName,
            providerIcon: state.logoUrl,
            flowMode: state.flowMode,
          },
          samlVerificationResponse: state.samlVerificationResponse,
        }
      : {}),
  };
};

export const SsoSettingsPage: FC<SsoSettingsParameters> = ({
  initialSettings,
  samlAvailable = false,
}) => {
  const [generalError, setGeneralError] = useState("");
  const [ssoInitiallyEnabled, setSsoInitiallyEnabled] = useState(
    initialSettings.ssoEnabled
  );
  const logoElement = useRef(null);
  const [logo, setLogo] = useState<File | null>(null);
  const [logoError, setLogoError] = useState("");
  const [logoSuccess, setLogoSuccess] = useState(false);
  const [logoSrc, setLogoSrc] = useState("");
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [formIsValid, setFormIsValid] = useState(false);
  const [formState, setFormState] = useState<SsoSettings>(
    fromSettingsRequest(initialSettings)
  );
  const [saving, setSaving] = useState<boolean>(false);

  // A signed test response is only required the first time a company sets up SAML, or when the
  // certificate is actually changing -- not for edits to unrelated fields (see
  // knowledge/decisions/flo-15511-saml-verification-and-save-flow.md and its FLO-15509 follow-up).
  // `initialSettings` never changes for the life of this component, so comparing against it
  // directly (rather than duplicating it into `formState`) is enough to detect an edit.
  const initialCertificate = initialSettings.samlProvider?.idpSigningCertificate ?? "";
  const certificateChanged =
    initialCertificate.trim() !== (formState.idpSigningCertificate ?? "").trim();
  const certificateOnFileNotAfter = initialSettings.samlProvider?.certificateNotAfter;
  const certificateOnFileRetiring =
    initialSettings.samlProvider?.certificateStatus === "RETIRING";

  useEffect(() => {
    evaluateFormValidity();
  }, [formState, logo]);

  useEffect(() => {
    if (!formState.ssoEnabled) {
      setFormState({
        ...formState,
        appleEnabled: false,
        facebookEnabled: false,
        googleEnabled: false,
        customEnabled: false,
      });
    }
  }, [formState.ssoEnabled]);

  const evaluateFormValidity = () => {
    const ssoEnabled = formState.ssoEnabled;
    const implementationEnabled =
      ssoEnabled &&
      (formState.facebookEnabled ||
        formState.googleEnabled ||
        formState.appleEnabled ||
        formState.customEnabled);
    // The Company SSO button config (company name + favicon) is required regardless of protocol
    // (FR-1: it renders identically for OIDC and SAML).
    const companyButtonFieldsNotBlank =
      isNotBlank(formState.companyName) && isNotBlank(formState.logoUrl);
    const oidcFieldsNotBlank =
      isNotBlank(formState.wellKnownEndpoint) &&
      isNotBlank(formState.clientId) &&
      isNotBlank(formState.clientSecret) &&
      isNotBlank(formState.apiKey);
    // FR-1: saving is blocked client-side until a Test Company SSO verification response has been
    // provided, when one is actually required -- the first time a company sets up SAML, or when
    // the certificate field has been edited from what was loaded (`certificateChanged`).
    // CompanySettingsService makes the same "is verification required" decision server-side
    // (ResponseMissing/verifyAndPersist), so this client-side check can't be more lenient than
    // what the server will actually accept -- it can only fail closed and ask for a response the
    // server didn't need. See knowledge/decisions/flo-15511-saml-verification-and-save-flow.md.
    const samlVerificationNotBlank =
      !certificateChanged || isNotBlank(formState.samlVerificationResponse);
    const samlFieldsNotBlank =
      isNotBlank(formState.idpEntityId) &&
      isNotBlank(formState.idpSsoUrl) &&
      isNotBlank(formState.idpSigningCertificate) &&
      isNotBlank(formState.spEntityId) &&
      isNotBlank(formState.acsUrl) &&
      samlVerificationNotBlank;
    // Guard against a stale "SAML" protocol value if the rollout flag is off (e.g. disabled after
    // a company was previously configured for SAML) -- the selector is flag-gated, so validation
    // must not require SAML-only fields when the selector itself isn't offered.
    const protocolFieldsNotBlank =
      samlAvailable && formState.protocol === "SAML"
        ? samlFieldsNotBlank
        : oidcFieldsNotBlank;
    const customSsoValid =
      !formState.customEnabled ||
      (companyButtonFieldsNotBlank && protocolFieldsNotBlank);

    if (!ssoEnabled || (implementationEnabled && customSsoValid)) {
      return setFormIsValid(true);
    }

    setFormIsValid(false);
  };

  const handleCheckboxChange = (event) => {
    setFormState({
      ...formState,
      [event.target.name]: event.target.checked,
    });
  };

  const handleInputChange = (event) => {
    setFormState({
      ...formState,
      [event.target.name]: event.target.value,
    });
  };

  const handleProtocolChange = (event) => {
    setFormState({
      ...formState,
      protocol: event.target.value as SsoProtocol,
    });
  };

  const handleFlowModeChange = (event) => {
    setFormState({
      ...formState,
      flowMode: event.target.value as SamlFlowMode,
    });
  };

  const handleLogoChange = (event) => {
    const chosen = event.target.files[0];

    setLogo(chosen);
    setLogoSrc(URL.createObjectURL(chosen));
  };

  const handleLogoLoaded = () => {
    if (logoElement.current == null) {
      return;
    }

    const { naturalHeight, naturalWidth } = logoElement.current;

    if (naturalHeight <= 32 && naturalWidth <= 32) {
      setLogoError("");
      setLogoSuccess(true);
      setFormState({
        ...formState,
        logoUrl: getDataUrl(logoElement.current, logo?.type),
      });
    } else {
      setLogo(null);
      setLogoSuccess(false);
      setLogoError("Uploaded file doesn’t meet logo requirements");
      setFormState({
        ...formState,
        logoUrl: ellipseGray,
      });
    }
  };

  const handleSaveSettingsClicked = () => {
    if (ssoInitiallyEnabled && !formState.ssoEnabled) {
      return setConfirmDisable(true);
    }

    saveSsoSettings();
  };

  const saveSsoSettings = async () => {
    const settingsRequest = toSettingsRequest(formState, samlAvailable);

    setSaving(true);

    try {
      const response = await saveCompanySsoSettings(settingsRequest);

      if (response.success) {
        document.cookie = `PLAY_FLASH=success=${encodeURI(
          "Borrower SSO settings have been saved"
        )}; path=/`;
        window.location.href = "/company-settings";
      } else {
        setSaving(false);
        setGeneralError(response.errors.exception);

        window.scrollTo(0, 0);
      }
    } catch (error) {
      setSaving(false);
      setGeneralError(
        "Borrower SSO settings have not been saved. Please contact support@floify.com for help."
      );
    }
  };

  return (
    <ThemeProvider theme={theme}>
      {generalError && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {generalError}
        </Alert>
      )}

      {/* Always shown */}
      <Box sx={{ mt: 3, mb: 3 }}>
        <Box>
          <Typography variant="h3">Borrower SSO settings </Typography>
          <Typography
            variant="body1"
            sx={{ color: theme.palette.gray[500], mb: 1 }}
          >
            Borrower single sign-on (SSO) provides additional log in and account
            creation options for borrowers (e.g. - with Google credentials).
            Traditional Floify log in with email and password will always be
            available.{" "}
          </Typography>
          <Link href="company-settings">Return to your settings</Link>
          <Box sx={{ mt: 3 }}>
            <FormGroup sx={{ pl: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    sx={{ mr: 1 }}
                    onChange={handleCheckboxChange}
                    name="ssoEnabled"
                    checked={formState.ssoEnabled}
                  />
                }
                label="Enable borrower SSO"
              />
            </FormGroup>
          </Box>
        </Box>
        {/* Show if enable borrower sso is checked */}
        {formState.ssoEnabled && (
          <>
            <Divider sx={{ mt: 3, mb: 3 }} />
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="body1"
                sx={{ mb: 2, color: theme.palette.gray[700] }}
              >
                Choose which SSO methods to make available to your borrowers:
              </Typography>
              <FormGroup sx={{ pl: 1, mb: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{ mr: 1 }}
                      onChange={handleCheckboxChange}
                      name="googleEnabled"
                      checked={formState.googleEnabled}
                    />
                  }
                  label="Google SSO"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{ mr: 1 }}
                      onChange={handleCheckboxChange}
                      name="facebookEnabled"
                      checked={formState.facebookEnabled}
                    />
                  }
                  label="Facebook SSO"
                />
                {initialSettings.appleAvailable && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        sx={{ mr: 1 }}
                        onChange={handleCheckboxChange}
                        name="appleEnabled"
                        checked={formState.appleEnabled}
                      />
                    }
                    label="Apple ID SSO"
                  />
                )}

                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{ mr: 1 }}
                      onChange={handleCheckboxChange}
                      name="customEnabled"
                      checked={formState.customEnabled}
                    />
                  }
                  label="Company SSO"
                />
              </FormGroup>
              <Typography
                variant="body1"
                sx={{
                  fontStyle: "italic",
                  color: theme.palette.gray[500],
                  mb: 4,
                }}
              >
                Company credentials can be used for SSO login if OpenID Connect
                has been implemented for Company authentication.
              </Typography>
            </Box>
            {/* Shown if company SSO is checked */}
            {formState.customEnabled && (
              <>
                <Divider sx={{ mt: 3, mb: 3 }} />
                <Box>
                  {samlAvailable && (
                    <Box sx={{ mb: 3 }}>
                      <FormLabel
                        id="sso-protocol-label"
                        sx={{
                          mb: 1,
                          display: "block",
                          color: theme.palette.gray[700],
                        }}
                      >
                        Company SSO protocol:
                      </FormLabel>
                      <RadioGroup
                        aria-labelledby="sso-protocol-label"
                        name="protocol"
                        value={formState.protocol}
                        onChange={handleProtocolChange}
                      >
                        <FormControlLabel
                          value="OIDC"
                          control={<Radio />}
                          label="OpenID Connect"
                        />
                        <FormControlLabel
                          value="SAML"
                          control={<Radio />}
                          label="SAML 2.0"
                        />
                      </RadioGroup>
                    </Box>
                  )}

                  <Typography
                    variant="body1"
                    sx={{ mb: 2, color: theme.palette.gray[700] }}
                  >
                    Company SSO required values:
                  </Typography>

                  {samlAvailable && formState.protocol === "SAML" ? (
                    <Grid container sx={{ mb: 3 }} spacing={2}>
                      <Grid item xs={12}>
                        <FormLabel
                          id="saml-flow-mode-label"
                          sx={{
                            mb: 1,
                            display: "block",
                            color: theme.palette.gray[700],
                          }}
                        >
                          Login flow supported by your IdP:
                        </FormLabel>
                        <RadioGroup
                          aria-labelledby="saml-flow-mode-label"
                          name="flowMode"
                          value={formState.flowMode ?? "SP_ONLY"}
                          onChange={handleFlowModeChange}
                        >
                          <FormControlLabel
                            value="SP_ONLY"
                            control={<Radio />}
                            label="SP-initiated only (borrower starts on Floify)"
                          />
                          <FormControlLabel
                            value="IDP_ONLY"
                            control={<Radio />}
                            label="IdP-initiated only (borrower starts on your IdP; the Continue button will be hidden on Floify)"
                          />
                          <FormControlLabel
                            value="BOTH"
                            control={<Radio />}
                            label="Both"
                          />
                        </RadioGroup>
                      </Grid>
                      <Grid item xs={6}>
                        <TextInput
                          label="IdP Entity ID / Issuer"
                          placeholder="Input text here"
                          fcsx={{ width: "100%" }}
                          onChange={handleInputChange}
                          name="idpEntityId"
                          value={formState.idpEntityId}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextInput
                          label="IdP SSO URL"
                          placeholder="Input text here"
                          fcsx={{ width: "100%" }}
                          onChange={handleInputChange}
                          name="idpSsoUrl"
                          value={formState.idpSsoUrl}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextInput
                          label="SP Entity ID"
                          placeholder="Input text here"
                          fcsx={{ width: "100%" }}
                          onChange={handleInputChange}
                          name="spEntityId"
                          value={formState.spEntityId}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextInput
                          label="ACS URL"
                          placeholder="Input text here"
                          fcsx={{ width: "100%" }}
                          onChange={handleInputChange}
                          name="acsUrl"
                          value={formState.acsUrl}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextInput
                          label="IdP X.509 Signing Certificate"
                          placeholder="Paste the IdP's PEM-encoded certificate here"
                          fcsx={{ width: "100%" }}
                          multiline
                          rows={4}
                          onChange={handleInputChange}
                          name="idpSigningCertificate"
                          value={formState.idpSigningCertificate}
                        />
                        {certificateOnFileNotAfter && (
                          <Typography
                            variant="caption"
                            sx={{ color: theme.palette.gray[700] }}
                          >
                            Certificate on file valid through{" "}
                            {new Date(certificateOnFileNotAfter).toLocaleDateString()}
                            {certificateOnFileRetiring && " (retiring)"}. Paste a
                            new certificate above to rotate it.
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <TextInput
                          label={
                            certificateChanged
                              ? "Test SAML response"
                              : "Test SAML response (not required)"
                          }
                          helperText={
                            certificateChanged
                              ? "Paste a signed SAML Response from your IdP. Required to verify this configuration before it can be saved."
                              : "Not required -- the certificate on file hasn't changed. Only needed when setting up SAML for the first time or rotating the certificate."
                          }
                          placeholder="Paste the signed SAML Response here"
                          fcsx={{ width: "100%" }}
                          multiline
                          rows={4}
                          onChange={handleInputChange}
                          name="samlVerificationResponse"
                          value={formState.samlVerificationResponse}
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <>
                      <Grid container sx={{ mb: 3 }} spacing={2}>
                        <Grid item xs={6}>
                          <TextInput
                            label="Client ID"
                            placeholder="Input text here"
                            fcsx={{ width: "100%" }}
                            onChange={handleInputChange}
                            name="clientId"
                            value={formState.clientId}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextInput
                            label="Client secret"
                            placeholder="Input text here"
                            fcsx={{ width: "100%" }}
                            onChange={handleInputChange}
                            name="clientSecret"
                            value={formState.clientSecret}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextInput
                            label="Well-known endpoint"
                            placeholder="Input text here"
                            fcsx={{ width: "100%" }}
                            onChange={handleInputChange}
                            name="wellKnownEndpoint"
                            value={formState.wellKnownEndpoint}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextInput
                            label="Key (Unique Identifier)"
                            placeholder="Input text here"
                            fcsx={{ width: "100%" }}
                            onChange={handleInputChange}
                            name="apiKey"
                            value={formState.apiKey}
                          />
                        </Grid>
                      </Grid>

                      <FormControlLabel
                        sx={{ mb: 3 }}
                        control={
                          <Checkbox
                            sx={{ mr: 1, pl: 1 }}
                            onChange={handleCheckboxChange}
                            name="pkceEnabled"
                            checked={formState.pkceEnabled}
                          />
                        }
                        label="Enable Proof Key for Code Exchange (PKCE) for this Company SSO"
                      />
                    </>
                  )}

                  <Typography
                    variant="body1"
                    sx={{ mb: 2, color: theme.palette.gray[700] }}
                  >
                    Company SSO button:
                  </Typography>
                  <TextInput
                    placeholder="Name Here"
                    label="Company name"
                    helperText="Max 18 Characters"
                    onChange={handleInputChange}
                    name="companyName"
                    value={formState.companyName}
                    inputProps={{
                      maxlength: 18,
                    }}
                  />

                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.gray[600], mb: 1, mt: 2 }}
                  >
                    Company Logo (favicon)
                  </Typography>
                  <Box>
                    <Stack direction="row">
                      <label htmlFor="contained-button-file">
                        <LogoInput
                          inputProps={{
                            accept:
                              "image/png,image/jpeg,image/svg+xml,image/ico,image/vnd.microsoft.icon,image/icon,image/x-icon",
                          }}
                          id="contained-button-file"
                          type="file"
                          onChange={handleLogoChange}
                        />
                        <PrimaryButton
                          component="span"
                          variant="contained"
                          startIcon={
                            <FontAwesomeIcon
                              icon={faUpload}
                              style={{ width: 18, height: 18 }}
                            />
                          }
                          sx={{ pl: 4, pr: 4 }}
                        >
                          Upload company logo
                        </PrimaryButton>
                      </label>
                      {logoError && (
                        <>
                          <FontAwesomeIcon
                            icon={faCircleExclamation}
                            style={{
                              width: 18,
                              height: 18,
                              paddingTop: 10,
                              paddingLeft: 8,
                              color: theme.palette.error[500],
                            }}
                          />
                          <Typography
                            variant="body1"
                            sx={{
                              pt: 1,
                              pl: 1,
                              color: theme.palette.error[500],
                            }}
                          >
                            {logoError}
                          </Typography>
                        </>
                      )}
                      {logoSuccess && (
                        <Stack direction="row" sx={{ ml: 2 }}>
                          <Stack alignContent="center" justifyContent="center">
                            <img
                              src={logoSrc}
                              style={{
                                maxHeight: "32px",
                                maxWidth: "32px",
                              }}
                            />
                          </Stack>

                          <Stack sx={{ ml: 0.8 }}>
                            <Box
                              component="span"
                              sx={{
                                color: theme.palette.gray[700],
                              }}
                            >
                              {logo?.name}
                            </Box>

                            <Typography
                              sx={{
                                lineHeight: "16px",
                                fontSize: "12px",
                                color: theme.palette.success[800],
                                whiteSpace: "nowrap",
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faCheck}
                                style={{
                                  marginRight: 4,
                                  width: 12,
                                  height: 12,
                                  color: theme.palette.success[800],
                                }}
                              />
                              Successfully uploaded
                            </Typography>
                          </Stack>
                        </Stack>
                      )}
                    </Stack>
                    <LogoImage
                      src={logoSrc}
                      ref={logoElement}
                      onLoad={handleLogoLoaded}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      mb: 2,
                      color: theme.palette.gray[500],
                      textDecoration: "italic",
                    }}
                  >
                    Image must be no larger than 32x32 pixels.
                  </Typography>

                  {/* Preview Section */}
                  <Typography
                    variant="body1"
                    sx={{ mb: 2, mt: 4, color: theme.palette.gray[700] }}
                  >
                    Company SSO preview:
                  </Typography>

                  <Stack direction="row">
                    <Stack sx={{ width: 400 }}>
                      <SsoButton
                        variant="outlined"
                        sx={{
                          mb: 1,
                          maxWidth: 280,
                        }}
                        size="large"
                        disableRipple={true}
                        startIcon={
                          <img
                            src={formState.logoUrl}
                            alt="company-logo"
                            style={{ width: 22, height: 22 }}
                          />
                        }
                      >
                        <SsoButtonText>
                          Continue with{" "}
                          {formState.companyName || "Company Name"}
                        </SsoButtonText>
                      </SsoButton>
                    </Stack>
                  </Stack>
                  <Typography
                    variant="caption"
                    sx={{
                      mb: 2,
                      color: theme.palette.gray[500],
                      textDecoration: "italic",
                    }}
                  >
                    This is a preview of how your Company SSO button will
                    display.
                  </Typography>
                </Box>
              </>
            )}
          </>
        )}
      </Box>

      {/* Always shown */}
      <Stack direction="row">
        <GrayButton
          sx={{
            mr: 1,
          }}
          variant="contained"
          onClick={() => (window.location.href = "/company-settings")}
        >
          Cancel
        </GrayButton>
        <PrimaryButton
          disabled={!formIsValid || saving}
          variant="contained"
          onClick={handleSaveSettingsClicked}
        >
          Save
        </PrimaryButton>
      </Stack>

      <FloModal
        aria-labelledby="modalLabel"
        open={confirmDisable}
        headerStyles={{}}
        headerContent={
          <Stack direction="row" alignItems="center" spacing={2}>
            <FontAwesomeIcon
              icon={faCircleExclamationDuo}
              style={{
                width: 34,
                height: 34,
                marginRight: 10,
                // @ts-ignore
                "--fa-primary-color": theme.palette.error[500],
                // @ts-ignore
                "--fa-secondary-color": theme.palette.error[300],
              }}
            />

            <Typography
              variant="h4"
              sx={{
                flex: 1,
              }}
            >
              Are you sure you want to disable borrower SSO?
            </Typography>

            <FontAwesomeIcon
              icon={faXmark}
              style={{
                width: 16,
                height: 16,
                color: theme.palette.gray[400],
                cursor: "pointer",
              }}
              onClick={() => setConfirmDisable(false)}
            />
          </Stack>
        }
      >
        <Typography
          variant="body1"
          sx={{
            mt: 3,
          }}
        >
          Disabling borrower SSO will require borrowers previously logging in
          with social SSO to create a new Floify password. Borrowers will no
          longer be able to log in with the SSO method that they were previously
          using.
        </Typography>

        <Stack
          direction="row"
          justifyContent="right"
          sx={{
            mt: 3,
          }}
        >
          <GrayButton
            sx={{ mr: 1 }}
            variant="contained"
            color="info"
            onClick={() => setConfirmDisable(false)}
          >
            Cancel
          </GrayButton>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setConfirmDisable(false);
              saveSsoSettings();
            }}
          >
            Disable borrower SSO
          </Button>
        </Stack>
      </FloModal>
    </ThemeProvider>
  );
};

const PrimaryButton = styled((props: { component?: string } & ButtonProps) => (
  <Button {...props} />
))<ButtonProps>`
  color: ${theme.palette.shades.white};

  &.Mui-disabled {
    opacity: 0.5;
    color: ${theme.palette.shades.white};
    background-color: ${theme.palette.primary[500]};
  }
`;

const GrayButton = styled(Button)`
  background-color: ${theme.palette.gray[200]} !important;
  color: ${theme.palette.shades.black} !important;

  &:hover {
    background-color: ${theme.palette.gray[300]} !important;
    color: ${theme.palette.shades.white};
  }
`;
